export enum HTMLMediaElementEvent {
  ABORT = 'abort',
  CANPLAY = 'canplay',
  CANPLAYTHROUGH = 'canplaythrough',
  DURATIONCHANGE = 'durationchange',
  EMPTIED = 'emptied',
  ENDED = 'ended',
  ERROR = 'error',
  LOADEDDATA = 'loadeddata',
  LOADEDMETADATA = 'loadedmetadata',
  LOADSTART = 'loadstart',
  PAUSE = 'pause',
  PLAY = 'play',
  PLAYING = 'playing',
  PROGRESS = 'progress',
  RATECHANGE = 'ratechange',
  SEEKED = 'seeked',
  SEEKING = 'seeking',
  STALLED = 'stalled',
  SUSPEND = 'suspend',
  TIMEUPDATE = 'timeupdate',
  VOLUMECHANGE = 'volumechange',
  WAITING = 'waiting',
}

export enum HTMLVideoElementEvent {
  ENTERPICTUREINPICTURE = 'enterpictureinpicture',
  LEAVEPICTUREINPICTURE = 'leavepictureinpicture',
}

export enum ElementEvent {
  FULLSCREENCHANGE = 'fullscreenchange',
  RESIZE = 'resize',
}

export enum VideoJsEvent {
  AUDIOONLYMODECHANGE = 'audioonlymodechange',
  AUDIOPOSTERMODECHANGE = 'audiopostermodechange',
  CONTROLSDISABLED = 'controlsdisabled',
  CONTROLSENABLED = 'controlsenabled',
  DEBUGON = 'debugon',
  DEBUGOFF = 'debugoff',
  DISABLEPICTUREINPICTURECHANGED = 'disablepictureinpicturechanged',
  DISPOSE = 'dispose',
  ENTERFULLWINDOW = 'enterFullWindow',
  ERROR = 'error',
  EXITFULLWINDOW = 'exitFullWindow',
  FIRSTPLAY = 'firstplay',
  FULLSCREENERROR = 'fullscreenerror',
  LANGUAGECHANGE = 'languagechange',
  LOADEDMETADATA = 'loadedmetadata',
  LOADSTART = 'loadstart',
  PLAYERRESET = 'playerreset',
  PLAYERRESIZE = 'playerresize',
  POSTERCHANGE = 'posterchange',
  READY = 'ready',
  TEXTDATA = 'textdata',
  USERACTIVE = 'useractive',
  USERINACTIVE = 'userinactive',
  USINGCUSTOMCONTROLS = 'usingcustomcontrols',
  USINGNATIVECONTROLS = 'usingnativecontrols',
  BEFOREPLUGINSETUP = 'beforepluginsetup',
  PLUGINSETUP = 'pluginsetup',
  TEXTTRACKCHANGE = 'texttrackchange',
  SOURCESET = 'sourceset', // required options.enableSourceset=true
  MOUSELEAVE = 'mouseleave',
  // below events seems not working
  // COMPONENTRESIZE = 'componentresize',
  // PLAYBACKRATESCHANGE = 'playbackrateschange',
  // TAP = 'tap',
}

export enum VideoJsComponentEvent {
  SLIDERINACTIVE = 'sliderinactive', // VolumeControl event
  SLIDERACTIVE = 'slideractive', // VolumeControl event
}

export enum LegacyTrackingEvent {
  STARTVIDEOAD = 'StartVideoAd',
}

export enum TrackingEvent {
  CLICK = 'click',
  REACH = 'reach',
  AD_EVENT = 'ad_event',
  START = 'start',
  RESUME = 'resume',
  PAUSE = 'pause',
  SEEK = 'seek',
  HEARTBEAT = 'heartbeat',
}

// https://videojs.github.io/videojs-contrib-ads/integrator/api.html
export enum ContribAdsEvents {
  ADSTART = 'adstart',
  ADEND = 'adend',
  ADSKIP = 'adskip', // this skip is triggered by contribs-ad when ima skip the ad for any reason, but not reilable
  ADSERROR = 'adserror',
  // events that contrib-ads talks to IMA
  READYFORPREROLL = 'readyforpreroll',
  READYFORPOSTROLL = 'readyforpostroll',
  ADTIMEOUT = 'adtimeout',
  CONTENTCHANGED = 'contentchanged',
  // events that IMA talks to contrib-ads
  ADSREADY = 'adsready',
  STARTLINEARADMODE = 'startLinearAdMode',
  ADS_AD_STARTED = 'ads-ad-started',
  NOPREROLL = 'nopreroll',
  NOPOSTROLL = 'nopostroll',
  CONTENTRESUMED = 'contentresumed',
  // deprecated
  CONTENTENDED = 'contentended',
}

// https://github.dev/googleads/videojs-ima/blob/11ecbefa37fbdbd6877fece63c38c11338b9e913/README.md#L206-L212
export enum VjsImaEvent {
  ADS_MANAGER = 'ads-manager',
  ADS_LOADER = 'ads-loader',
  ADS_REQUEST = 'ads-request',
  CONTENT_PAUSE_REQUESTED = 'contentPauseRequested',
  CONTENT_RESUME_REQUESTED = 'contentResumeRequested',
}

export enum CustomEvent {
  X_SOURCENOTCHANGED = 'x:sourcenotchanged',
  X_CLICK_CLOSEBTN = 'x:click:closebtn',
  X_CLICK_BIGPLAYBTN = 'x:click:bigplaybtn',
  X_CLICK_PLAYTOGGLE = 'x:click:playtoggle',
  X_CLICK_QUALITYLEVEL = 'x:click:qualitylevel',
  X_CLICK_SUBTITLE = 'x:click:subtitle',
  X_CLICK_POSTER = 'x:click:poster',
  X_CLICK_USERVOLUMECHANGE = 'x:click:uservolumechange',
  X_PLUGININIT_QUALITYLEVELSMANAGERPLUGIN = 'x:plugininit:qualityLevelsManagerPlugin',
  X_AD_ERROR_EVENT = 'x:adderrorevent',
  // since v3 or before
  CLICKTOPLAY = 'clickToPlay',
  CLICKTOPAUSE = 'clickToPause',
  SEEKMOUSEDOWN = 'seekMouseDown',
  SEEKMOUSEUP = 'seekMouseUp',
  INLINESCREEN = 'inlineScreen',
  FULLSCREEN = 'fullScreen',
  UNMUTE = 'unmute',
  MUTE = 'mute',
  VOLUMEBARSLIDERINACTIVE = 'volumeBarSliderInactive',
  VOLUMEBARSLIDERACTIVE = 'volumeBarSliderActive',
}

export enum NonPlayerTargetEvent {
  X_PLAYER_CREATED = 'x:player:created',
  X_MENU_SHOW = 'x:menu:show',
  X_MENU_HIDE = 'x:menu:hide',
}

export const Event = {
  ...HTMLMediaElementEvent,
  ...HTMLVideoElementEvent,
  ...ElementEvent,
  ...VideoJsEvent,
  ...VideoJsComponentEvent,
  ...TrackingEvent,
  ...ContribAdsEvents,
  ...VjsImaEvent,
  ...CustomEvent,
  ...NonPlayerTargetEvent,
};

export type EventMap = typeof Event;

export type EventKey = keyof typeof Event;

export type EventName = (typeof Event)[EventKey] | LegacyTrackingEvent;

export type EventData = Record<string, unknown>;

export enum LabelKey {
  // common fields
  // TYPE = 'type',
  SESSION_ID = 'session_id',
  VIDEO_ID = 'video_id',
  VIDEO_URL = 'video_url', // new in v5
  VERSION = 'version',
  IS_LIVE = 'is_live',
  TS = 'ts',
  URL = 'url',
  // fields vary in different events
  IS_PAUSED = 'is_paused',
  IS_AUTO_PLAY = 'is_auto_play',
  IS_MUTED = 'is_muted',
  VIDEO_TIME = 'video_time',
  VIDEO_LENGTH = 'video_length',
  VIDEO_FROM_TIME = 'video_from_time',
  VIDEO_TO_TIME = 'video_to_time',
  BUTTON = 'button', // previously as buttonName in v3 or before
  ADID = 'adID',
  ADTYPE = 'adType',
  AD_SEC_FROM_START = 'ad_sec_from_start',
  NUM_AD_STARTED = 'num_ad_started',
  // ad_event related
  EVENT_TYPE = 'event_type',
  // ad error related
  AD_ERROR_CODE = 'ad_error_code',
  AD_INNER_ERROR = 'ad_inner_error',
  AD_ERROR_MESSAGE = 'ad_error_message',
  AD_ERROR_TYPE = 'ad_error_type',
  AD_VAST_ERROR_CODE = 'ad_vast_error_code',
}

export enum AdEventType {
  // custom ad event_type
  STARTVIDEOAD = 'StartVideoAd',
  ENDVIDEOAD = 'EndVideoAd',
  LOADVIDEOADTIMEOUT = 'LoadVideoAdTimeout',
  VIDEOADERROR = 'VideoAdError',

  // from google.ima.AdEvent.Type
  SKIPPED = 'skip',
  USER_CLOSE = 'userClose',
  CLICK = 'click',
  IMPRESSION = 'impression',
  MIDPOINT = 'midpoint',
  COMPLETE = 'complete',
  ALL_ADS_COMPLETED = 'allAdsCompleted',
}
